import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
} from '@mui/material';
import {
  ExpandMore,
  ExpandLess,
  PersonSearch,
} from '@mui/icons-material';
import { viewsAPI } from '../services/api';
import { ProspectViewDetail, ProspectViewSummary, SearchFormData } from '../types';

interface HomeViewsPanelProps {
  onUseMember: (data: Partial<SearchFormData>) => void;
  onOpenFullView: () => void;
}

// Addresses are stored as "street, city, ST zip" (see migration 009).
export const parseViewAddress = (address: string | null): Partial<SearchFormData> => {
  if (!address) return {};
  const parts = address.split(',').map(p => p.trim()).filter(Boolean);
  const result: Partial<SearchFormData> = { STREET1: parts[0] ?? '' };
  if (parts.length >= 2) result.CITY = parts[1];
  if (parts.length >= 3) {
    const [state, ...zipParts] = parts[2].split(/\s+/);
    result.STATE = state ?? '';
    result.ZIP = zipParts.join(' ');
  }
  return result;
};

const HomeViewsPanel: React.FC<HomeViewsPanelProps> = ({ onUseMember, onOpenFullView }) => {
  const [expanded, setExpanded] = useState(true);
  const [views, setViews] = useState<ProspectViewSummary[]>([]);
  const [selectedViewId, setSelectedViewId] = useState<number | ''>('');
  const [detail, setDetail] = useState<ProspectViewDetail | null>(null);
  const [loadingList, setLoadingList] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoadingList(true);
    viewsAPI.listViews()
      .then(data => {
        if (cancelled) return;
        setViews(data);
        if (data.length > 0) setSelectedViewId(data[0].id);
      })
      .catch(err => { if (!cancelled) setError(err.message || 'Failed to load views'); })
      .finally(() => { if (!cancelled) setLoadingList(false); });
    return () => { cancelled = true; };
  }, []);

  const loadDetail = useCallback(async (viewId: number) => {
    setLoadingDetail(true);
    setError(null);
    try {
      setDetail(await viewsAPI.getView(viewId));
    } catch (err: any) {
      setError(err.message || 'Failed to load view members');
      setDetail(null);
    } finally {
      setLoadingDetail(false);
    }
  }, []);

  useEffect(() => {
    if (selectedViewId === '') {
      setDetail(null);
      return;
    }
    loadDetail(selectedViewId);
  }, [selectedViewId, loadDetail]);

  const handleUse = (member: ProspectViewDetail['members'][number]) => {
    onUseMember({
      FIRST_NAME: member.first_name ?? '',
      LAST_NAME: member.last_name ?? '',
      ...parseViewAddress(member.address),
    });
  };

  return (
    <Box>
      <Box
        onClick={() => setExpanded(e => !e)}
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', mb: expanded ? 2 : 0 }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem' }}>
            My Views
          </Typography>
        </Box>
        {expanded ? <ExpandLess /> : <ExpandMore />}
      </Box>

      {expanded && (
        <Box>
          {loadingList ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={24} sx={{ color: '#283E56' }} />
            </Box>
          ) : views.length === 0 ? (
            <Typography variant="caption" sx={{ color: '#999', fontStyle: 'italic' }}>
              No views yet. Create one from Batch Screening.
            </Typography>
          ) : (
            <>
              <Tabs
                value={selectedViewId}
                onChange={(_, value: number) => setSelectedViewId(value)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                  mb: 1.5,
                  minHeight: 36,
                  '& .MuiTab-root': { minHeight: 36, textTransform: 'none', fontSize: '0.8rem', fontWeight: 600, py: 0.5, px: 1.5, color: '#566573' },
                  '& .Mui-selected': { color: '#2E3B55' },
                  '& .MuiTabs-indicator': { backgroundColor: '#2E3B55' },
                }}
              >
                {views.map(v => (
                  <Tab key={v.id} value={v.id} label={v.name} />
                ))}
              </Tabs>

              {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}

              {loadingDetail ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                  <CircularProgress size={24} sx={{ color: '#283E56' }} />
                </Box>
              ) : detail && detail.members.length > 0 ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, maxHeight: 240, overflowY: 'auto', pr: 0.5 }}>
                  {detail.members.map(m => (
                    <Box
                      key={m.id}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        p: 1.5,
                        borderRadius: 1.5,
                        backgroundColor: 'rgba(52, 152, 219, 0.04)',
                        border: '1px solid rgba(52, 152, 219, 0.08)',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          backgroundColor: 'rgba(52, 152, 219, 0.08)',
                          borderColor: 'rgba(52, 152, 219, 0.15)',
                        },
                      }}
                    >
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#2C3E50', fontSize: '0.85rem', mb: 0.3 }}>
                          {[m.first_name, m.last_name].filter(Boolean).join(' ')}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#566573', fontSize: '0.75rem', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {m.address ?? 'No address'}
                        </Typography>
                      </Box>
                      <Tooltip title="Copy into Search Information">
                        <IconButton
                          size="small"
                          onClick={() => handleUse(m)}
                          sx={{
                            color: '#2E3B55',
                            backgroundColor: 'white',
                            border: '1px solid #e0e0e0',
                            '&:hover': { backgroundColor: '#2E3B55', color: 'white' },
                          }}
                        >
                          <PersonSearch fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  ))}
                </Box>
              ) : (
                !error && (
                  <Typography variant="caption" sx={{ color: '#999', fontStyle: 'italic' }}>
                    This view has no members.
                  </Typography>
                )
              )}

              <Typography
                variant="caption"
                onClick={onOpenFullView}
                sx={{ color: '#3498db', cursor: 'pointer', mt: 1.5, display: 'block', fontWeight: 600, textAlign: 'center', '&:hover': { textDecoration: 'underline' } }}
              >
                Open all views
              </Typography>
            </>
          )}
        </Box>
      )}
    </Box>
  );
};

export default HomeViewsPanel;
